import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { settlementQuoteQueryOptions } from '@/api/loans-api'
import type { CashSource, RepaymentPosted, SettlementQuote } from '@/api/loans-types'
import type { DualControlResult } from '@/api/types'
import { ErrorSurface } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatShortDate } from '@/lib/datetime'
import { formatMoney, type Pesewas } from '@/lib/money'

import { PostingDayNotice } from './business-day'
import { useBusinessDate } from './use-business-day'
import {
  CASH_SOURCE_LABELS,
  EARLY_SETTLEMENT_LABELS,
  newClientOperationId,
} from './constants'
import { DualControlNotice, DualControlOutcome } from './dual-control'
import { LOAN_PERMISSIONS, useLoanPermissions } from './permissions'
import { loanPaths, useLoanNavigate } from './loan-paths'
import {
  AmountField,
  AppliedOutcome,
  DateField,
  FormField,
  NoteField,
  OperationForm,
} from './operation-form'
import type { CycleOperationContext } from './operation-screen'
import {
  useDisburseRound,
  usePostRepayment,
  useRecordRecovery,
  useSettleRound,
} from './mutations'

/**
 * The screens where money actually moves.
 *
 * Every one of them shares three habits.
 *
 * **The value date comes from the ledger.** It is seeded from the branch's business date and
 * never from the browser's clock, and when no day is open the screen says so and the field is
 * left empty rather than filled with today — a date the ledger would refuse after the member has
 * already handed over the cash.
 *
 * **The till is not asked for.** `tillId` is the Tellering module's own sequential identifier and
 * nothing the API publishes exposes it: the tills endpoint answers with a UUID and a till number.
 * The back office therefore sends null, which the contract allows, and the cash leg is recorded
 * without a till dimension. Cash taken at a counter belongs on the teller screen, where the
 * module that owns custody supplies it.
 *
 * **A posting carries an idempotency key.** A double click, a retried request, or a resent form
 * settles once and answers with the original result.
 */

function CashSourceField({
  id,
  value,
  onChange,
  label = 'Where the cash is',
}: {
  id: string
  value: CashSource
  onChange: (value: CashSource) => void
  label?: string
}) {
  return (
    <FormField
      id={id}
      label={label}
      required
      hint="Recorded as a journal dimension. The till itself is supplied by the teller screen, not here."
    >
      <Select value={value} onValueChange={(next) => onChange(next as CashSource)}>
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(Object.keys(CASH_SOURCE_LABELS) as CashSource[]).map((source) => (
            <SelectItem key={source} value={source}>
              {CASH_SOURCE_LABELS[source]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FormField>
  )
}

/** What a posting actually settled, bucket by bucket. Computed by the server, never re-derived. */
function AllocationSummary({ posted }: { posted: RepaymentPosted }) {
  const currency = posted.currencyCode

  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
        {(
          [
            ['Penalty', posted.allocation.penaltyMinor],
            ['Fees', posted.allocation.feeMinor],
            ['Interest', posted.allocation.interestMinor],
            ['Principal', posted.allocation.principalMinor],
          ] as const
        ).map(([label, amount]) => (
          <div key={label}>
            <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</dt>
            <dd>
              <MoneyDisplay value={amount} currency={currency} className="text-left" />
            </dd>
          </div>
        ))}
      </dl>

      {posted.allocation.lines.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Instalment</TableHead>
              <TableHead>Bucket</TableHead>
              <TableHead className="text-right">Applied</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {posted.allocation.lines.map((line, index) => (
              <TableRow key={`${line.instalmentNumber}-${line.bucket}-${index}`}>
                <TableCell className="numeric tabular-nums">{line.instalmentNumber}</TableCell>
                <TableCell>{line.bucket}</TableCell>
                <TableCell>
                  <MoneyDisplay value={line.amountMinor} currency={currency} symbol={false} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}

      <p className="text-2xs text-muted">
        Oldest instalment first, then the product&apos;s allocation order within it. Journal{' '}
        {posted.journalEntry ? (
          <span className="font-mono">{posted.journalEntry}</span>
        ) : (
          'not raised for this posting'
        )}
        .
      </p>
    </div>
  )
}

/**
 * Releasing the money.
 *
 * Dual-controlled, and refused to whoever approved the round — which is what makes three people
 * the minimum before cash leaves the institution.
 */
export function DisburseForm({ cycle, accountId, cycleId }: CycleOperationContext) {
  const navigate = useLoanNavigate()
  const permissions = useLoanPermissions()
  const businessDay = useBusinessDate()
  const disburse = useDisburseRound(cycleId, accountId)

  const [disbursementDate, setDisbursementDate] = useState('')
  const [firstRepaymentDate, setFirstRepaymentDate] = useState(
    cycle.terms.firstRepaymentDate ?? '',
  )
  const [cashSource, setCashSource] = useState<CashSource>('TellerTill')
  const [reference, setReference] = useState('')
  const [result, setResult] = useState<DualControlResult | null>(null)

  // The business date arrives a moment after the screen does. Seeding on first sight of it rather
  // than in state initialisation is what stops the field being permanently empty when the query
  // resolves second — and it never overwrites a date the operator has already keyed.
  const effectiveDisbursementDate =
    disbursementDate === '' ? (businessDay.date ?? '') : disbursementDate

  const currency = cycle.summary.currencyCode
  const valid = effectiveDisbursementDate !== '' && firstRepaymentDate !== ''

  return (
    <div className="space-y-4">
      <PostingDayNotice />

      <OperationForm
        title="Release the money"
        description="Generates the schedule and posts the disbursement to the ledger."
        submitLabel="Disburse"
        isPending={disburse.isPending}
        disabled={!valid}
        error={disburse.isError ? disburse.error : null}
        onDone={() => navigate(loanPaths.cycle(accountId, cycleId))}
        notice={
          <DualControlNotice
            action="Releasing the money"
            approvePermission={LOAN_PERMISSIONS.disburseApprove}
            canCountersign={permissions.canCountersignDisbursement}
          />
        }
        outcome={
          result ? (
            <DualControlOutcome
              result={result}
              awaitingDescription="The disbursement is stored and no money has moved. The round is held at Pending disbursement until it is countersigned."
              appliedDescription="The money is released, the schedule is generated and the journal is posted. The round is live."
            />
          ) : null
        }
        onSubmit={() => {
          if (!valid) {
            return
          }

          disburse.mutate(
            {
              disbursementDate: effectiveDisbursementDate,
              cashSource,
              tillId: null,
              firstRepaymentDate,
              reference: reference.trim() === '' ? null : reference.trim(),
            },
            { onSuccess: setResult },
          )
        }}
        footnote="Whoever approved this round cannot release it. The server refuses that outright, whatever the approval policy says."
      >
        <Alert tone="info">
          <AlertTitle>
            Approved for{' '}
            {cycle.approvedAmountMinor === null
              ? 'an amount this round does not carry'
              : formatMoney(cycle.approvedAmountMinor, { currency })}
          </AlertTitle>
          <AlertDescription>
            Fees collected by deduction come out of what the member is handed; capitalised fees are
            added to what they owe. The round&apos;s fees tab lists both.
          </AlertDescription>
        </Alert>

        <div className="grid gap-4 sm:grid-cols-2">
          <DateField
            id="disburse-date"
            label="Disbursement date"
            value={effectiveDisbursementDate}
            onChange={setDisbursementDate}
            hint={
              businessDay.date === null
                ? 'No business day is open, so this was not filled in for you.'
                : "Filled in from the branch's business date."
            }
          />

          <DateField
            id="disburse-first-repayment"
            label="First repayment"
            value={firstRepaymentDate}
            onChange={setFirstRepaymentDate}
            hint="It cannot fall before the disbursement. The rest of the schedule is generated from it."
          />

          <CashSourceField id="disburse-source" value={cashSource} onChange={setCashSource} />

          <NoteField
            id="disburse-reference"
            label="Reference"
            value={reference}
            onChange={setReference}
            placeholder="Cheque number, transfer reference, receipt"
            hint="What the money left against, for the branch's own reconciliation."
          />
        </div>
      </OperationForm>
    </div>
  )
}

/** A collection. The split across penalty, fees, interest and principal is the server's. */
export function RepaymentForm({ cycle, account, accountId, cycleId }: CycleOperationContext) {
  const navigate = useLoanNavigate()
  const businessDay = useBusinessDate()
  const repay = usePostRepayment(cycleId, accountId)

  const [amount, setAmount] = useState<Pesewas | null>(null)
  const [valueDate, setValueDate] = useState('')
  const [channel, setChannel] = useState<CashSource>('TellerTill')
  const [narration, setNarration] = useState('')
  const [operationId, setOperationId] = useState(newClientOperationId)
  const [posted, setPosted] = useState<RepaymentPosted | null>(null)

  const effectiveValueDate = valueDate === '' ? (businessDay.date ?? '') : valueDate
  const currency = cycle.summary.currencyCode
  const valid = amount !== null && amount > 0 && effectiveValueDate !== ''
  const exceedsOutstanding = amount !== null && amount > cycle.summary.totalOutstandingMinor

  return (
    <div className="space-y-4">
      <PostingDayNotice />

      <OperationForm
        title="Post a repayment"
        description="Applied oldest instalment first, in the product's allocation order within it."
        submitLabel="Post repayment"
        isPending={repay.isPending}
        disabled={!valid}
        error={repay.isError ? repay.error : null}
        onDone={() => navigate(loanPaths.cycle(accountId, cycleId))}
        outcome={
          posted ? (
            <div className="space-y-3">
              <AppliedOutcome
                title={
                  posted.cycleClosed
                    ? 'Posted — and that closed the round'
                    : 'Posted'
                }
              >
                <MoneyDisplay
                  value={posted.amountMinor}
                  currency={posted.currencyCode}
                  className="inline"
                />{' '}
                collected with a value date of {formatShortDate(posted.valueDate)}. The round now
                owes{' '}
                <MoneyDisplay
                  value={posted.cycle.totalOutstandingMinor}
                  currency={posted.currencyCode}
                  className="inline"
                />
                .
              </AppliedOutcome>
              <AllocationSummary posted={posted} />
            </div>
          ) : null
        }
        onSubmit={() => {
          if (!valid) {
            return
          }

          repay.mutate(
            {
              amountMinor: amount,
              valueDate: effectiveValueDate,
              channel,
              tillId: null,
              clientOperationId: operationId,
              // No meeting is named from the back office. A collection taken at a meeting is
              // captured against it by the field application or the bulk collection sheet, both
              // of which know which meeting they are in; inventing one here would file a counter
              // payment under a register nobody sat in.
              groupMeetingId: null,
              narration: narration.trim() === '' ? null : narration.trim(),
            },
            {
              onSuccess: (result) => {
                setPosted(result)
                // A fresh key for whatever the operator does next, so a second, genuinely
                // different collection is not mistaken for a replay of this one.
                setOperationId(newClientOperationId())
              },
            },
          )
        }}
        footnote={`Idempotency key ${operationId}. A replay of this exact posting settles once.`}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <AmountField
            id="repay-amount"
            label="Amount collected"
            currency={currency}
            value={amount}
            onChange={setAmount}
            autoFocus
            hint={
              <>
                Outstanding {formatMoney(cycle.summary.totalOutstandingMinor, { currency })}
                {cycle.summary.amountInArrearsMinor > 0 ? (
                  <>
                    , of which {formatMoney(cycle.summary.amountInArrearsMinor, { currency })} is in
                    arrears
                  </>
                ) : null}
                .
              </>
            }
          />

          <DateField
            id="repay-value-date"
            label="Value date"
            value={effectiveValueDate}
            onChange={setValueDate}
            hint={
              businessDay.date === null
                ? 'No business day is open, so this was not filled in for you.'
                : "The branch's business date, not today's."
            }
          />

          <CashSourceField id="repay-channel" value={channel} onChange={setChannel} label="Channel" />

          <NoteField
            id="repay-narration"
            label="Narration"
            value={narration}
            onChange={setNarration}
            placeholder="Receipt number, or what the member said"
          />
        </div>

        {exceedsOutstanding ? (
          <Alert tone="warning">
            <AlertTitle>More than the round owes</AlertTitle>
            <AlertDescription>
              The round owes{' '}
              <MoneyDisplay
                value={cycle.summary.totalOutstandingMinor}
                currency={currency}
                className="inline"
              />
              . The server refuses an overpayment (
              <span className="font-mono text-xs">loans.repayment_exceeds_outstanding</span>) —
              surplus has to be routed deliberately, into savings or as a settlement, rather than
              landing here.
            </AlertDescription>
          </Alert>
        ) : null}

        {account?.groupId ? (
          <p className="text-2xs text-muted">
            This account belongs to a group. A collection taken at a meeting should be captured
            against that meeting rather than here, so the attendance and collection records agree.
          </p>
        ) : null}
      </OperationForm>
    </div>
  )
}

/**
 * Clearing a round early, at the quoted figure.
 *
 * The quote is read first and sent back with the settlement: a settlement whose amount has moved
 * since the member was quoted is refused rather than silently repriced. That is the whole point
 * of `expectedSettlementAmountMinor`, so this screen never lets an operator key one.
 */
export function SettlementForm({ accountId, cycleId }: CycleOperationContext) {
  const navigate = useLoanNavigate()
  const businessDay = useBusinessDate()
  const settle = useSettleRound(cycleId, accountId)

  const [asOf, setAsOf] = useState('')
  const [channel, setChannel] = useState<CashSource>('TellerTill')
  const [narration, setNarration] = useState('')
  const [operationId, setOperationId] = useState(newClientOperationId)
  const [posted, setPosted] = useState<RepaymentPosted | null>(null)

  const effectiveAsOf = asOf === '' ? (businessDay.date ?? '') : asOf
  const quote = useQuery(
    settlementQuoteQueryOptions(cycleId, effectiveAsOf === '' ? null : effectiveAsOf),
  )

  return (
    <div className="space-y-4">
      <PostingDayNotice />

      <OperationForm
        title="Settle the round early"
        description="At the quoted figure, under the product's rebate rule."
        submitLabel="Take the settlement"
        isPending={settle.isPending}
        disabled={!quote.isSuccess || effectiveAsOf === ''}
        error={settle.isError ? settle.error : null}
        onDone={() => navigate(loanPaths.cycle(accountId, cycleId))}
        outcome={
          posted ? (
            <div className="space-y-3">
              <AppliedOutcome title={posted.cycleClosed ? 'Settled and closed' : 'Settled'}>
                <MoneyDisplay
                  value={posted.amountMinor}
                  currency={posted.currencyCode}
                  className="inline"
                />{' '}
                taken with a value date of {formatShortDate(posted.valueDate)}.
                {posted.cycleClosed
                  ? ' The round is closed. A further round starts as a new application.'
                  : ' The round is still open — check what remains against the schedule.'}
              </AppliedOutcome>
              <AllocationSummary posted={posted} />
            </div>
          ) : null
        }
        onSubmit={() => {
          if (!quote.isSuccess || effectiveAsOf === '') {
            return
          }

          settle.mutate(
            {
              valueDate: effectiveAsOf,
              expectedSettlementAmountMinor: quote.data.settlementAmountMinor,
              channel,
              tillId: null,
              clientOperationId: operationId,
              narration: narration.trim() === '' ? null : narration.trim(),
            },
            {
              onSuccess: (result) => {
                setPosted(result)
                setOperationId(newClientOperationId())
              },
            },
          )
        }}
        footnote="The figure sent is the quote below, not a keyed amount. If it has moved since the member was quoted, the server refuses rather than repricing."
      >
        <DateField
          id="settle-as-of"
          label="Value date"
          value={effectiveAsOf}
          onChange={setAsOf}
          hint={
            businessDay.date === null
              ? 'No business day is open, so this was not filled in for you. The quote is computed as at this date.'
              : "The branch's business date. The quote is computed as at this date."
          }
        />

        {quote.isPending ? (
          <Skeleton className="h-40 w-full" />
        ) : quote.isError ? (
          <ErrorSurface
            error={quote.error}
            onRetry={() => void quote.refetch()}
            title="The settlement could not be quoted"
          />
        ) : (
          <SettlementQuotePanel
            quote={quote.data}
            asOfLabel={effectiveAsOf === '' ? null : formatShortDate(effectiveAsOf)}
          />
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <CashSourceField id="settle-channel" value={channel} onChange={setChannel} label="Channel" />
          <NoteField
            id="settle-narration"
            label="Narration"
            value={narration}
            onChange={setNarration}
            placeholder="Receipt number"
          />
        </div>
      </OperationForm>
    </div>
  )
}

function SettlementQuotePanel({
  quote,
  asOfLabel,
}: {
  quote: SettlementQuote
  asOfLabel: string | null
}) {
  const currency = quote.currencyCode

  return (
    <div className="space-y-3 rounded-card border border-border-subtle bg-surface-muted p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-heading">
          Settlement {asOfLabel ? `as at ${asOfLabel}` : 'quote'}
        </p>
        <MoneyDisplay
          value={quote.settlementAmountMinor}
          currency={currency}
          emphasis="strong"
          className="text-right text-base"
        />
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
        <div>
          <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">Principal</dt>
          <dd>
            <MoneyDisplay
              value={quote.principalOutstandingMinor}
              currency={currency}
              className="text-left"
            />
          </dd>
        </div>
        <div>
          <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">
            Interest payable
          </dt>
          <dd>
            <MoneyDisplay
              value={quote.interestPayableMinor}
              currency={currency}
              className="text-left"
            />
          </dd>
        </div>
        <div>
          <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">Fees</dt>
          <dd>
            <MoneyDisplay
              value={quote.feesOutstandingMinor}
              currency={currency}
              className="text-left"
            />
          </dd>
        </div>
        <div>
          <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">Penalty</dt>
          <dd>
            <MoneyDisplay
              value={quote.penaltyOutstandingMinor}
              currency={currency}
              className="text-left"
            />
          </dd>
        </div>
      </dl>

      <p className="text-xs text-body">
        {quote.rule === null ? (
          <>
            The product names no early-settlement rule, so nothing is assumed about a rebate. The
            figure above is what the server quoted, and it is the figure that will be taken.
          </>
        ) : (
          <>
            {EARLY_SETTLEMENT_LABELS[quote.rule]} —{' '}
            <MoneyDisplay
              value={quote.interestRebatedMinor}
              currency={currency}
              className="inline"
            />{' '}
            of interest is not charged because the round is being cleared early.
          </>
        )}
      </p>
    </div>
  )
}

/**
 * Cash recovered after a write-off.
 *
 * The round stays written off: a recovery is income against a debt the institution has already
 * given up on, not a resurrection of the loan.
 */
export function RecoveryForm({ cycle, accountId, cycleId }: CycleOperationContext) {
  const navigate = useLoanNavigate()
  const businessDay = useBusinessDate()
  const recovery = useRecordRecovery(cycleId, accountId)

  const [amount, setAmount] = useState<Pesewas | null>(null)
  const [valueDate, setValueDate] = useState('')
  const [channel, setChannel] = useState<CashSource>('TellerTill')
  const [narration, setNarration] = useState('')
  const [done, setDone] = useState(false)

  const effectiveValueDate = valueDate === '' ? (businessDay.date ?? '') : valueDate
  const currency = cycle.summary.currencyCode
  const valid = amount !== null && amount > 0 && effectiveValueDate !== ''

  return (
    <div className="space-y-4">
      <PostingDayNotice />

      <OperationForm
        title="Record a recovery"
        description="Cash collected against a round that has already been written off."
        submitLabel="Record recovery"
        isPending={recovery.isPending}
        disabled={!valid}
        error={recovery.isError ? recovery.error : null}
        onDone={() => navigate(loanPaths.cycle(accountId, cycleId))}
        outcome={
          done ? (
            <AppliedOutcome title="Recovery recorded">
              The round stays written off. The recovery is income against a debt already given up
              on, and the round&apos;s recovered figure now includes it.
            </AppliedOutcome>
          ) : null
        }
        onSubmit={() => {
          if (!valid) {
            return
          }

          recovery.mutate(
            {
              amountMinor: amount,
              valueDate: effectiveValueDate,
              channel,
              tillId: null,
              narration: narration.trim() === '' ? null : narration.trim(),
            },
            { onSuccess: () => setDone(true) },
          )
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <AmountField
            id="recovery-amount"
            label="Amount recovered"
            currency={currency}
            value={amount}
            onChange={setAmount}
            autoFocus
            hint={
              <>
                Written off {formatMoney(cycle.writtenOffAmountMinor, { currency })}; recovered so
                far {formatMoney(cycle.recoveredAmountMinor, { currency })}.
              </>
            }
          />

          <DateField
            id="recovery-value-date"
            label="Value date"
            value={effectiveValueDate}
            onChange={setValueDate}
            hint={
              businessDay.date === null
                ? 'No business day is open, so this was not filled in for you.'
                : "The branch's business date, not today's."
            }
          />

          <CashSourceField
            id="recovery-channel"
            value={channel}
            onChange={setChannel}
            label="Channel"
          />

          <NoteField
            id="recovery-narration"
            label="Narration"
            value={narration}
            onChange={setNarration}
            placeholder="Receipt number, or how it was collected"
          />
        </div>
      </OperationForm>
    </div>
  )
}
